<template>
  <div class="global-error-handler">
    <!-- 全局加载遮罩 -->
    <Teleport to="body">
      <div
        v-if="errorHandlerStore.globalLoading"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
          <n-spin size="large" class="mb-4" />
          <p class="text-gray-600">Loading, please wait...</p>
        </div>
      </div>
    </Teleport>

    <!-- 网络状态提示 -->
    <Teleport to="body">
      <Transition name="slide-down">
        <div
          v-if="!errorHandlerStore.networkStatus.isOnline"
          class="fixed top-0 left-0 right-0 bg-red-500 text-white text-center py-2 z-40"
        >
          <div class="flex items-center justify-center space-x-2">
            <n-icon :component="WifiOff" />
            <span>The network connection has been disconnected. Please check your network settings.</span>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 错误通知列表 -->
    <div class="fixed top-4 right-4 space-y-2 z-30 max-w-sm">
      <TransitionGroup name="error-notification">
        <div
          v-for="error in visibleErrors"
          :key="error.id"
          class="bg-white border-l-4 rounded-lg shadow-lg p-4"
          :class="getErrorBorderColor(error.type)"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-1">
                <n-icon 
                  :component="getErrorIcon(error.type)" 
                  :class="getErrorIconColor(error.type)"
                />
                <span class="font-medium text-sm text-gray-900">
                  {{ getErrorTitle(error.type) }}
                </span>
              </div>
              
              <p class="text-sm text-gray-600 mb-2">
                {{ error.message }}
              </p>
              
              <p v-if="error.suggestion" class="text-xs text-gray-500 mb-3">
                {{ error.suggestion }}
              </p>
              
              <div class="flex items-center space-x-2">
                <n-button
                  v-if="error.retryable && error.retryCount < error.maxRetries"
                  size="tiny"
                  type="primary"
                  @click="retryError(error.id)"
                  :loading="retryingErrors.has(error.id)"
                >
                  Retry ({{ error.retryCount }}/{{ error.maxRetries }})
                </n-button>
                
                <n-button
                  size="tiny"
                  quaternary
                  @click="dismissError(error.id)"
                >
                  Ignore
                </n-button>
              </div>
            </div>
            
            <n-button
              size="tiny"
              quaternary
              circle
              @click="dismissError(error.id)"
              class="ml-2"
            >
              <template #icon>
                <n-icon :component="X" />
              </template>
            </n-button>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- 批量操作按钮 -->
    <div
      v-if="errorHandlerStore.retryableErrors.length > 1"
      class="fixed bottom-4 right-4 z-30"
    >
      <div class="bg-white rounded-lg shadow-lg p-3 border">
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-600">
            {{ errorHandlerStore.retryableErrors.length }} This error can be retried
          </span>
          <n-button
            size="small"
            type="primary"
            @click="retryAllErrors"
            :loading="retryingAll"
          >
            Retry all
          </n-button>
          <n-button
            size="small"
            quaternary
            @click="dismissAllErrors"
          >
            Ignore all
          </n-button>
        </div>
      </div>
    </div>

    <!-- 网络恢复提示 -->
    <Teleport to="body">
      <Transition name="slide-up">
        <div
          v-if="showNetworkRecovery"
          class="fixed bottom-4 left-4 bg-green-500 text-white rounded-lg p-3 shadow-lg z-30"
        >
          <div class="flex items-center space-x-2">
            <n-icon :component="Wifi" />
            <span>The network connection has been restored</span>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { NButton, NIcon, NSpin } from 'naive-ui'
import { 
  AlertCircle, 
  Wifi, 
  WifiOff, 
  Clock, 
  Shield, 
  Server, 
  AlertTriangle,
  X
} from '@vicons/tabler'
import { useErrorHandlerStore } from '@/stores/errorHandler'
import type { NetworkErrorType } from '@/types/api'

const errorHandlerStore = useErrorHandlerStore()

// 本地状态
const retryingErrors = ref<Set<string>>(new Set())
const retryingAll = ref(false)
const showNetworkRecovery = ref(false)
const dismissedErrors = ref<Set<string>>(new Set())

// 计算属性
const visibleErrors = computed(() => 
  errorHandlerStore.errors
    .filter(error => !dismissedErrors.value.has(error.id))
    .slice(0, 5) // 最多显示5个错误
)

// 错误图标映射
const getErrorIcon = (type: NetworkErrorType) => {
  switch (type) {
    case 'TIMEOUT':
      return Clock
    case 'CONNECTION_ERROR':
      return WifiOff
    case 'AUTH_ERROR':
      return Shield
    case 'VALIDATION_ERROR':
      return AlertTriangle
    case 'SERVER_ERROR':
      return Server
    default:
      return AlertCircle
  }
}

// 错误图标颜色
const getErrorIconColor = (type: NetworkErrorType): string => {
  switch (type) {
    case 'AUTH_ERROR':
    case 'SERVER_ERROR':
      return 'text-red-500'
    case 'TIMEOUT':
    case 'CONNECTION_ERROR':
      return 'text-orange-500'
    case 'VALIDATION_ERROR':
      return 'text-yellow-500'
    default:
      return 'text-gray-500'
  }
}

// 错误边框颜色
const getErrorBorderColor = (type: NetworkErrorType): string => {
  switch (type) {
    case 'AUTH_ERROR':
    case 'SERVER_ERROR':
      return 'border-red-500'
    case 'TIMEOUT':
    case 'CONNECTION_ERROR':
      return 'border-orange-500'
    case 'VALIDATION_ERROR':
      return 'border-yellow-500'
    default:
      return 'border-gray-500'
  }
}

// 错误标题
const getErrorTitle = (type: NetworkErrorType): string => {
  switch (type) {
    case 'TIMEOUT':
      return 'TIME OUT'
    case 'CONNECTION_ERROR':
      return 'CONNECTION ERROR'
    case 'AUTH_ERROR':
      return 'AUTH ERROR'
    case 'VALIDATION_ERROR':
      return 'VALIDATION ERROR'
    case 'SERVER_ERROR':
      return 'SERVER ERROR'
    default:
      return 'UNKNOWN ERROR'
  }
}

// 方法
const retryError = async (errorId: string): Promise<void> => {
  retryingErrors.value.add(errorId)
  try {
    await errorHandlerStore.retryError(errorId)
  } finally {
    retryingErrors.value.delete(errorId)
  }
}

const retryAllErrors = async (): Promise<void> => {
  retryingAll.value = true
  try {
    await errorHandlerStore.retryAllErrors()
  } finally {
    retryingAll.value = false
  }
}

const dismissError = (errorId: string): void => {
  dismissedErrors.value.add(errorId)
  // 延迟移除，让动画完成
  setTimeout(() => {
    errorHandlerStore.removeError(errorId)
    dismissedErrors.value.delete(errorId)
  }, 300)
}

const dismissAllErrors = (): void => {
  errorHandlerStore.clearErrors()
  dismissedErrors.value.clear()
}

// 监听网络状态变化
let networkRecoveryTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => errorHandlerStore.networkStatus.isOnline,
  (isOnline, wasOnline) => {
    if (isOnline && wasOnline === false) {
      // 网络恢复
      showNetworkRecovery.value = true
      
      // 3秒后隐藏提示
      if (networkRecoveryTimer) {
        clearTimeout(networkRecoveryTimer)
      }
      networkRecoveryTimer = setTimeout(() => {
        showNetworkRecovery.value = false
      }, 3000)
    }
  }
)

// 生命周期
onMounted(() => {
  errorHandlerStore.initNetworkMonitoring()
})

onUnmounted(() => {
  if (networkRecoveryTimer) {
    clearTimeout(networkRecoveryTimer)
  }
})
</script>

<style scoped>
/* 错误通知动画 */
.error-notification-enter-active,
.error-notification-leave-active {
  transition: all 0.3s ease;
}

.error-notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.error-notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.error-notification-move {
  transition: transform 0.3s ease;
}

/* 滑动动画 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>